import {useLocation, useNavigate} from "react-router-dom";
import {Grid, Table, TableBody, TableCell, TableContainer, TableRow, Typography} from "@mui/material";
import I18n from "i18n-js";
import Card from "@mui/material/Card";
import {Button, Field, FieldsContainer, Form, Spacer, TextField} from "../../../common";
import useOrderForm from "./useOrderForm";
import React, {useEffect} from "react";
import {Box} from "@mui/system";
import {getPaymentInformation} from "../billing-and-plans/manage-payments/ManagePaymentsApi";
import {OrderForm} from "./orderAPI";

export default function Order() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    priceBreakdown,
    item,
    errors,
    loading,
    onChange,
    processForm,
    loadPriceBreakdown,
  } = useOrderForm();

  const {
    bundleId,
    bundleName,
    bundleType,
    userLimit,
    waiverLimit,
    storageLimit,
    submitLimit,
    locationLimit,
  } = location.state || {};

  useEffect(() => {
    if (!location.state) {
      navigate("/", {replace: true});
      return;
    }

    if (bundleId) {
      loadPriceBreakdown(bundleId);
    }

    item.bundle_id = bundleId;
    item.type = bundleType;

    async function loadPaymentInformation() {
      const data = await getPaymentInformation();

      onChange({
        ...item,
        firstName: data.billing_first_name,
        lastName: data.billing_last_name,
        street: data.billing_street,
        city: data.billing_city,
        country: data.billing_country,
        state: data.billing_state,
        zip: data.billing_zip,
        phone: data.billing_phone,
      } as OrderForm);
    }

    loadPaymentInformation();
  }, [bundleId, bundleType]);

  if (!location.state) {
    return (<></>);
  }

  return (
    <Box>
      <Typography variant="h1">
        {I18n.t("MyAccount.pages.billingAndPlans.order.order")}
      </Typography>
      <Spacer h={2}/>
      <Form
        item={item}
        errors={errors}
        onChange={onChange}
        translationCategory="OrderForm.attributes"
      >
        <Grid container spacing={4}>
          <Grid item md={7} xs={12}>
            <Card
              variant="outlined"
              sx={{
                padding: "15px",
              }}
            >
              <Typography variant="h3">
                {I18n.t("MyAccount.pages.order.paymentInformation")}
              </Typography>
              <Spacer h={2}/>
              <FieldsContainer>
                <Field xs={12} md={6}>
                  <TextField id="firstName"/>
                </Field>
                <Field xs={12} md={6}>
                  <TextField id="lastName"/>
                </Field>
              </FieldsContainer>
              <Spacer h={2}/>
              <Typography variant="h5">
                {I18n.t("MyAccount.pages.order.billingAddress")}
              </Typography>
              <Spacer h={2}/>
              <FieldsContainer>
                <Field xs={12} md={6}>
                  <TextField id="street"/>
                </Field>
                <Field xs={12} md={6}>
                  <TextField id="city"/>
                </Field>
              </FieldsContainer>
              <Spacer h={2}/>
              <FieldsContainer>
                <Field xs={12} md={4}>
                  <TextField id="country"/>
                </Field>
                <Field xs={12} md={4}>
                  <TextField id="state"/>
                </Field>
                <Field xs={12} md={4}>
                  <TextField id="zip"/>
                </Field>
              </FieldsContainer>
              <Spacer h={2}/>
              <FieldsContainer>
                <Field xs={12} md={12}>
                  <TextField id="phone"/>
                </Field>
              </FieldsContainer>
            </Card>
          </Grid>
          <Grid item md={5} xs={12}>
            <Card
              variant="outlined"
              sx={{
                padding: "15px",
              }}
            >
              <Typography variant="h3">
                {I18n.t("MyAccount.pages.order.orderDetails")}
              </Typography>
              <Spacer h={2}/>
              <TableContainer>
                <Table sx={{width: '100%', borderCollapse: 'collapse'}}>
                  <TableBody>
                    <TableRow sx={{borderBottom: '1px solid #e0e0e0'}}>
                      <TableCell sx={{border: 0, padding: 0}}>
                        <Typography>{I18n.t("MyAccount.pages.order.orderDetailsTitle", {
                          bundleName,
                          bundlePeriod: I18n.t("MyAccount.pages.billingAndPlans.bundlesPeriod." + bundleType.toLowerCase()),
                        })}</Typography>
                        <Box sx={{marginTop: "5px", marginBottom: "10px"}}>
                          <Typography variant="body2" color="text.secondary">
                            - {I18n.t("MyAccount.pages.order.limits.userLimit", {
                            userLimit,
                          })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            - {I18n.t("MyAccount.pages.order.limits.waiverLimit", {
                            waiverLimit,
                          })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            - {I18n.t("MyAccount.pages.order.limits.storageLimit", {
                            storageLimit,
                          })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            - {I18n.t("MyAccount.pages.order.limits.submitLimit", {
                            submitLimit,
                          })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            - {I18n.t("MyAccount.pages.order.limits.locationLimit", {
                            locationLimit,
                          })}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{border: 0}}>
                        <Typography>{priceBreakdown.price || 0}</Typography>
                      </TableCell>
                    </TableRow>

                    {priceBreakdown.adjustments && priceBreakdown.adjustments.map((adjustment: Record<string, any>, index: any) => {
                      return (
                        <TableRow key={index} sx={{borderBottom: '1px solid #e0e0e0'}}>
                          <TableCell sx={{border: 0, padding: 0}}>
                            <Typography>
                              {adjustment.description}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{border: 0}}>
                            <Typography>
                              {adjustment.price}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    <TableRow>
                      <TableCell sx={{border: 0, padding: 0}}>
                        <Typography fontWeight="bold">Order total</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{border: 0}}>
                        <Typography fontWeight="bold">{priceBreakdown.totalPrice || 0}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
        <Spacer h={2}/>
        <Typography variant={"body1"}>
          {I18n.t("MyAccount.pages.order.yourNextBillingDate", {
            bundlePeriod: I18n.t("MyAccount.pages.billingAndPlans.bundlesPeriod." + bundleType.toLowerCase()).toLowerCase(),
            date: "2025 September 14",
          })}
        </Typography>
        <Spacer h={2}/>
        <Box sx={{display: "flex", justifyContent: "flex-end"}}>
          <Button
            loading={loading}
            onClick={() => processForm()}
          >
            {I18n.t("MyAccount.pages.order.placeOrder")}
          </Button>
        </Box>
      </Form>
    </Box>
  );
}